import { useRef } from "react";

import { IconMapPin } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { useIsTouchDevice } from "utilities/useIsTouchDevice";

import {
    type AddressEntity,
    type AddressFormValues,
    EntityMappingStatus,
    getAddressPlainText,
    getPinTypeForMap,
    type LocationEntity,
} from "commonComponents/entity/address/Address/Address.api.types";
import AddressMapTooltip from "commonComponents/entity/address/Address/AddressMapTooltip";
import { InfoWindowContent } from "commonComponents/entity/map/InfoWindowContent";
import { type IMapPosition, Mark, PinTypes } from "commonComponents/entity/map/Map.types";
import { MapButton } from "commonComponents/entity/map/MapButton/MapButton";
import { AddressActionSheet } from "commonComponents/utilities/AddressActionSheet/AddressActionSheet";

interface IAddressMapButtonProps {
    isMapOpenOnLoad?: boolean;
    mapPinTooltipHeader?: string;
    mapPinTooltipBody?: string;
    mapDefaultStartPosition?: IMapPosition;
    entityName?: string;
    address: AddressEntity;
    formValues?: AddressFormValues;
    addressId: string;
    location?: LocationEntity;
    onChange?: (addressId: string, value: any) => void;
    readOnly?: boolean;
    className?: string;
}

const AddressMapButton = (props: IAddressMapButtonProps) => {
    const {
        isMapOpenOnLoad,
        mapPinTooltipHeader,
        mapPinTooltipBody,
        mapDefaultStartPosition,
        entityName,
        address,
        formValues,
        addressId,
        location,
        onChange,
        readOnly,
        className,
    } = props;

    const isTouchPrimary = useIsTouchDevice();
    const mapButtonRef = useRef<MapButton>(null);

    const onMarkSet = (newPosition: IMapPosition | null) => {
        const newLocation: LocationEntity = {
            mapPosition: newPosition,
            mappingStatus:
                newPosition !== null
                    ? EntityMappingStatus.MappedManually
                    : EntityMappingStatus.NotMapped,
            hasAutoCompleteLocation: false,
        };
        onChange?.(addressId, { ...formValues, location: newLocation });
    };

    const pinType = location ? getPinTypeForMap(location) : undefined;
    const mark =
        location?.mapPosition && pinType !== PinTypes.None
            ? new Mark({
                  id: "addressPin",
                  position: location.mapPosition,
                  pinType: pinType!,
                  animate: false,
                  infoWindowContent: (
                      <InfoWindowContent
                          pinType={pinType}
                          header={mapPinTooltipHeader}
                          body={mapPinTooltipBody}
                          footer={<AddressMapTooltip address={address} formValues={formValues} />}
                      />
                  ),
              })
            : undefined;

    const renderMapButton = (hideTrigger: boolean) => (
        <MapButton
            ref={hideTrigger ? mapButtonRef : undefined}
            hideTrigger={hideTrigger}
            data-testid="addressMapButton"
            type="link"
            className={cn("MapButton", className)}
            mark={mark}
            entityName={entityName}
            isOpenWithMap={isMapOpenOnLoad}
            onMarkSet={readOnly ? undefined : onMarkSet}
            onMarkClear={readOnly ? undefined : () => onMarkSet(null)}
            defaultMapStartPosition={mapDefaultStartPosition}
            readOnly={readOnly}
        />
    );

    // On mouse-primary devices the map pin opens the MapModal directly (existing
    // behavior). On touch devices it instead presents an action sheet so the
    // address can also be copied; "View on map" opens the same MapModal.
    if (!isTouchPrimary) {
        return renderMapButton(false);
    }

    return (
        <>
            <AddressActionSheet
                copyText={getAddressPlainText(address)}
                testIdBase="addressMapButton"
                mapAction={{ kind: "modal", onOpenMap: () => mapButtonRef.current?.openModal() }}
            >
                <span
                    className={cn("MapButton", className, "inline-flex items-center")}
                    aria-label="Map pin"
                >
                    <IconMapPin size="md" />
                </span>
            </AddressActionSheet>
            {renderMapButton(true)}
        </>
    );
};

export default AddressMapButton;
